import { styled } from "@mui/material/styles";
import { ReactNode } from "react";
import ResizablePanel from "_shared/_views/resizeable-panel/resizable-panel";
import { usePanelLayoutPreference } from "_shared/_views/panels/use-panel-layout-preference";
import CollapsibleTemplatePanel from "work/proposals/_views/_shared/collapsible-template-panel";
import { paneIsCompact } from "work/_views/work-pane-layout";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";

const TemplatePanelContainer = styled(ResizablePanel, {
  shouldForwardProp: (prop) => prop !== "minimized",
})<{ minimized: boolean }>(({ theme, minimized }) => ({
  display: "flex",
  position: "relative",
  ...(minimized && {
    width: "auto !important",
    "& > div:last-of-type": {
      display: "none",
    },
  }),
  [paneIsCompact(theme)]: {
    flexShrink: 1,
    minWidth: 0,
    width: "100% !important",
    "& > div:first-of-type": {
      overflow: "visible",
    },
    "& > div:last-of-type": {
      display: "none",
    },
  },
}));

/**
 * The narrowest the templates column can be dragged to (#6621). A template's name wraps under it and
 * its tags fold into "+N" (#6609), so it reads at this width; the tab's own content keeps its 320px.
 */
export const TEMPLATE_PANEL_MIN_WIDTH = 240;

/** What the column opens at for a reader who never dragged it: room for a name, not for the minimum. */
export const TEMPLATE_PANEL_OPENING_WIDTH = 290;

/**
 * The one key every template tab keeps its column under, so the column does not change width or
 * come back open from one tab to the next.
 */
export const TEMPLATE_PANEL_KEY = "proposal-templates";

type ResizableTemplatePanelProps = {
  children: ReactNode;
  /** Lets a tab dress the column — an opacity for a waived collection, say — without restating its layout. */
  className?: string;
  minimized: boolean;
  selectedCount?: number;
};

/** A proposal template column with one consistent width, collapsed rail, and drag affordance. */
export default function ResizableTemplatePanel(props: Readonly<ResizableTemplatePanelProps>) {
  /**
   * The rail is what a *column* collapses to, so it is only ever offered while there is a column.
   *
   * Above `md` the panel sits beside the tab's content and a side panel opening takes the row's
   * width from it, which is what the rail trades away. A compact pane has no such column: the panel
   * is already stacked above the content as `TemplateSelector`'s own collapsed accordion — a bordered
   * summary you press to open — and there is nothing left for a rail to save. Asking for both left a
   * 44px vertical rail alone in a full-width band, with the accordion's line between the two sections
   * gone and the band's other 800px empty.
   */
  const paneIsCompact = useWorkPaneIsCompact();
  // Two reasons to show the rail, kept apart (#6621): the reader put the column away, which is theirs
  // and is remembered; or a side pane opened and needs the room, which lasts as long as the pane. When
  // the pane closes, the column goes back to however the reader keeps it.
  const layout = usePanelLayoutPreference(TEMPLATE_PANEL_KEY);
  const collapsedByReader = layout.isCollapsed && !paneIsCompact;
  const minimizedForSidePane = props.minimized && !paneIsCompact;
  const minimized = collapsedByReader || minimizedForSidePane;

  return (
    <TemplatePanelContainer
      className={props.className}
      initialWidth={layout.width ?? TEMPLATE_PANEL_OPENING_WIDTH}
      minWidth={TEMPLATE_PANEL_MIN_WIDTH}
      minRemainingWidth={320}
      minimized={minimized}
      onSnapToRail={() => layout.setCollapsed(true)}
      onWidthCommitted={layout.setWidth}
    >
      <CollapsibleTemplatePanel
        collapsedByReader={collapsedByReader}
        minimized={minimized}
        selectedCount={props.selectedCount}
        // A compact pane stacks the panel as an accordion, which already folds away; there is no
        // column to put away there.
        onCollapsedChange={paneIsCompact ? undefined : layout.setCollapsed}
      >
        {props.children}
      </CollapsibleTemplatePanel>
    </TemplatePanelContainer>
  );
}
